import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  ChevronLeft, ChevronRight, Clock, CheckCircle2, XCircle, Award,
  ArrowLeft, Send, AlertTriangle, RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';
import { getAuthToken } from '../lib/authToken';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============================================================
// Timer Component
// ============================================================
const Timer = ({ totalSeconds, onTimeUp }) => {
  const [remaining, setRemaining] = useState(totalSeconds);
  const onTimeUpRef = useRef(onTimeUp);
  onTimeUpRef.current = onTimeUp;

  useEffect(() => {
    if (remaining <= 0) { onTimeUpRef.current(); return; }
    const interval = setInterval(() => setRemaining(prev => prev - 1), 1000);
    return () => clearInterval(interval);
  }, [remaining]);

  const mins = Math.floor(remaining / 60);
  const secs = remaining % 60;
  const isLow = remaining < 60;

  return (
    <div className={`flex items-center gap-2 font-mono text-lg font-bold ${isLow ? 'text-red-600 animate-pulse' : 'text-slate-700'}`}>
      <Clock className="w-5 h-5" />
      {String(mins).padStart(2, '0')}:{String(secs).padStart(2, '0')}
    </div>
  );
};

// ============================================================
// Results Screen
// ============================================================
const ResultsScreen = ({ passage, qcm, onRetry, onBack }) => {
  const settings = qcm?.settings || {};
  const showCorrections = settings.afficher_corrections !== false;
  const corrections = passage?.details_corrections || [];
  const questionsMap = {};
  (qcm?.questions || []).forEach(q => { questionsMap[q.id] = q; });

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="qcm-results-screen">
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Score Card */}
        <div className={`rounded-2xl p-8 text-center mb-8 shadow-lg ${
          passage.reussi === true ? 'bg-gradient-to-br from-emerald-500 to-emerald-600 text-white' :
          passage.reussi === false ? 'bg-gradient-to-br from-red-500 to-red-600 text-white' :
          'bg-gradient-to-br from-indigo-500 to-indigo-600 text-white'
        }`}>
          <div className="mb-4">
            {passage.reussi === true ? (
              <CheckCircle2 className="w-16 h-16 mx-auto mb-2 opacity-90" />
            ) : passage.reussi === false ? (
              <XCircle className="w-16 h-16 mx-auto mb-2 opacity-90" />
            ) : (
              <Award className="w-16 h-16 mx-auto mb-2 opacity-90" />
            )}
          </div>
          <h1 className="text-3xl font-bold mb-2">
            {passage.reussi === true ? 'Félicitations !' : passage.reussi === false ? 'Non réussi' : 'Terminé !'}
          </h1>
          <div className="text-5xl font-black my-4">{passage.pourcentage}%</div>
          <p className="text-lg opacity-90">{passage.score} / {passage.score_max} points</p>
          {passage.reussi !== null && qcm?.settings?.seuil_reussite && (
            <p className="text-sm opacity-75 mt-2">Seuil de réussite : {qcm.settings.seuil_reussite}%</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-center gap-3 mb-8">
          <Button variant="outline" onClick={onBack} data-testid="results-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Retour
          </Button>
          {onRetry && (
            <Button variant="outline" onClick={onRetry}>
              <RotateCcw className="w-4 h-4 mr-2" /> Réessayer
            </Button>
          )}
        </div>

        {/* Corrections */}
        {showCorrections && corrections.length > 0 && (
          <div className="space-y-4" data-testid="corrections-list">
            <h2 className="text-lg font-semibold text-slate-800 mb-4">Corrections détaillées</h2>
            {corrections.map((c, idx) => {
              const q = questionsMap[c.question_id];
              if (!q) return null;
              return (
                <div key={c.question_id} className={`rounded-xl border p-5 ${c.est_correct ? 'border-emerald-200 bg-emerald-50/50' : 'border-red-200 bg-red-50/50'}`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-white text-sm font-bold ${c.est_correct ? 'bg-emerald-500' : 'bg-red-500'}`}>
                      {idx + 1}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-slate-800 mb-3">{q.texte}</p>
                      {q.image_id && (
                        <img
                          src={`${API_URL}/qcm-images/${q.image_id}`}
                          alt="Illustration de la question"
                          className="max-h-64 rounded-lg border border-slate-200 mb-3"
                          data-testid={`question-image-${q.id}`}
                        />
                      )}
                      <div className="space-y-1.5">
                        {q.options.map(o => {
                          const wasSelected = c.reponses_donnees.includes(o.id);
                          const isCorrect = c.bonnes_reponses.includes(o.id);
                          let style = 'bg-white border-slate-200';
                          if (isCorrect && wasSelected) style = 'bg-emerald-100 border-emerald-400';
                          else if (isCorrect && !wasSelected) style = 'bg-emerald-50 border-emerald-300 border-dashed';
                          else if (!isCorrect && wasSelected) style = 'bg-red-100 border-red-400';
                          return (
                            <div key={o.id} className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${style}`}>
                              {isCorrect ? <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" /> : wasSelected ? <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" /> : <div className="w-4 h-4 flex-shrink-0" />}
                              <span className={isCorrect ? 'font-medium' : ''}>{o.texte}</span>
                            </div>
                          );
                        })}
                      </div>
                      {c.explication && (
                        <p className="text-sm text-slate-600 mt-3 pl-2 border-l-2 border-indigo-300 italic">{c.explication}</p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================
// Main Passage Page
// ============================================================
export const QCMPassagePage = () => {
  const { qcmId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const candidatId = searchParams.get('candidat_id');
  const dossierId = searchParams.get('dossier_id');
  const returnUrl = searchParams.get('return') || '/qcm';

  const [qcm, setQcm] = useState(null);
  const [passage, setPassage] = useState(null);
  const [completedPassage, setCompletedPassage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});  // { question_id: [option_ids] }
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const timerRef = useRef(null);
  const token = getAuthToken();
  const headers = React.useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const startPassage = useCallback(async (qcmData) => {
    try {
      const res = await axios.post(`${API_URL}/qcms/${qcmId}/start`, {
        candidat_id: candidatId, dossier_id: dossierId,
      }, { headers });
      setPassage(res.data);
      // Initialize answers
      const initialAnswers = {};
      qcmData.questions.forEach(q => { initialAnswers[q.id] = []; });
      setAnswers(initialAnswers);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Impossible de démarrer le QCM');
      navigate(returnUrl);
    }
  }, [qcmId, candidatId, dossierId, headers, navigate, returnUrl]);

  useEffect(() => {
    const init = async () => {
      try {
        const res = await axios.get(`${API_URL}/qcms/${qcmId}/preview`, { headers });
        const qcmData = res.data;
        if (qcmData.statut !== 'publie') {
          toast.error('Ce QCM n\'est pas encore publié');
          navigate(returnUrl);
          return;
        }
        // Shuffle if needed
        let questions = [...qcmData.questions];
        if (qcmData.settings?.melanger_questions) {
          questions = questions.sort(() => Math.random() - 0.5);
        }
        if (qcmData.settings?.melanger_options) {
          questions = questions.map(q => ({
            ...q,
            options: [...q.options].sort(() => Math.random() - 0.5),
          }));
        }
        setQcm({ ...qcmData, questions });
        await startPassage(qcmData);
      } catch {
        toast.error('QCM introuvable');
        navigate(returnUrl);
      }
      setLoading(false);
    };
    init();
  }, [qcmId, startPassage, navigate, returnUrl, headers]);

  const handleSubmitRef = useRef(null);

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    setConfirmSubmit(false);
    try {
      const reponses = Object.entries(answers).map(([question_id, option_ids]) => ({
        question_id,
        option_ids,
      }));
      const res = await axios.put(`${API_URL}/passages-qcm/${passage.id}/submit`, { reponses }, { headers });
      let fullQcm = qcm;
      try {
        const fullRes = await axios.get(`${API_URL}/qcms/${qcmId}`, { headers });
        fullQcm = fullRes.data;
      } catch { /* use preview qcm if can't get full */ }
      setQcm(fullQcm);
      setCompletedPassage(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la soumission');
    }
    setSubmitting(false);
  };
  handleSubmitRef.current = handleSubmit;

  const handleTimeUp = useCallback(() => {
    toast.warning('Temps écoulé ! Soumission automatique...');
    handleSubmitRef.current();
  }, []);

  const toggleOption = (questionId, optionId, questionType) => {
    setAnswers(prev => {
      const current = prev[questionId] || [];
      if (questionType === 'choix_unique' || questionType === 'vrai_faux') {
        return { ...prev, [questionId]: [optionId] };
      }
      // choix_multiple
      if (current.includes(optionId)) {
        return { ...prev, [questionId]: current.filter(id => id !== optionId) };
      }
      return { ...prev, [questionId]: [...current, optionId] };
    });
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-500">Chargement du QCM...</p>
        </div>
      </div>
    );
  }

  // Results screen
  if (completedPassage) {
    const settings = qcm?.settings || {};
    if (settings.afficher_resultats === false) {
      return (
        <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center" data-testid="qcm-submitted-no-results">
          <div className="text-center px-6">
            <CheckCircle2 className="w-20 h-20 text-emerald-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-slate-800 mb-2">Réponses envoyées !</h1>
            <p className="text-slate-500 mb-6">Vos réponses ont été enregistrées avec succès.</p>
            <Button onClick={() => navigate(returnUrl)} data-testid="return-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Retour
            </Button>
          </div>
        </div>
      );
    }
    return (
      <ResultsScreen
        passage={completedPassage}
        qcm={qcm}
        onBack={() => navigate(returnUrl)}
        onRetry={null}
      />
    );
  }

  if (!qcm || !passage) return null;

  const questions = qcm.questions;
  const settings = qcm.settings || {};
  const currentQ = questions[currentIndex];
  const answeredCount = Object.values(answers).filter(a => a.length > 0).length;
  const allAnswered = answeredCount === questions.length;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="qcm-passage-page">
      {/* Top Bar */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(returnUrl)} data-testid="passage-back-btn">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-sm font-semibold text-slate-800 line-clamp-1">{qcm.titre}</h1>
              <p className="text-xs text-slate-400">{answeredCount}/{questions.length} répondu{answeredCount > 1 ? 'es' : ''}</p>
            </div>
          </div>
          {settings.temps_limite_minutes && (
            <Timer
              totalSeconds={settings.temps_limite_minutes * 60}
              onTimeUp={handleTimeUp}
            />
          )}
        </div>
        {/* Progress Bar */}
        <div className="h-1 bg-slate-100">
          <div
            className="h-full bg-indigo-500 transition-all duration-300"
            style={{ width: `${((currentIndex + 1) / questions.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Question Area */}
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Question navigation dots */}
        {settings.navigation_libre !== false && (
          <div className="flex flex-wrap gap-2 mb-8 justify-center" data-testid="question-nav-dots">
            {questions.map((q, i) => {
              const isAnswered = (answers[q.id] || []).length > 0;
              const isCurrent = i === currentIndex;
              return (
                <button
                  key={q.id}
                  onClick={() => setCurrentIndex(i)}
                  className={`w-8 h-8 rounded-full text-xs font-bold transition-all ${
                    isCurrent ? 'bg-indigo-600 text-white scale-110 shadow-md' :
                    isAnswered ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' :
                    'bg-slate-100 text-slate-400 hover:bg-slate-200'
                  }`}
                  data-testid={`nav-dot-${i}`}
                >
                  {i + 1}
                </button>
              );
            })}
          </div>
        )}

        {/* Current Question */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 md:p-8 mb-8" data-testid="current-question">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 text-indigo-600 flex items-center justify-center font-bold flex-shrink-0">
              {currentIndex + 1}
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-800">{currentQ.texte}</h2>
              <p className="text-xs text-slate-400 mt-1">
                {currentQ.type === 'choix_unique' || currentQ.type === 'vrai_faux' ? 'Sélectionnez une réponse' : 'Sélectionnez une ou plusieurs réponses'}
                {currentQ.points > 1 && ` — ${currentQ.points} points`}
              </p>
            </div>
          </div>

          {/* Options */}
          <div className="space-y-3 pl-0 md:pl-14">
            {currentQ.options.map((option) => {
              const isSelected = (answers[currentQ.id] || []).includes(option.id);
              return (
                <button
                  key={option.id}
                  onClick={() => toggleOption(currentQ.id, option.id, currentQ.type)}
                  className={`w-full text-left px-5 py-4 rounded-xl border-2 transition-all text-sm font-medium ${
                    isSelected
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-800 shadow-sm'
                      : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                  data-testid={`option-${option.id}`}
                >
                  <div className="flex items-center gap-3">
                    {currentQ.type === 'choix_multiple' ? (
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? 'border-indigo-500 bg-indigo-500' : 'border-slate-300'}`}>
                        {isSelected && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                      </div>
                    ) : (
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? 'border-indigo-500' : 'border-slate-300'}`}>
                        {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-indigo-500" />}
                      </div>
                    )}
                    {option.texte}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => setCurrentIndex(prev => Math.max(0, prev - 1))}
            disabled={currentIndex === 0 || settings.navigation_libre === false}
            data-testid="prev-question-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Précédent
          </Button>

          {currentIndex < questions.length - 1 ? (
            <Button
              onClick={() => setCurrentIndex(prev => Math.min(questions.length - 1, prev + 1))}
              data-testid="next-question-btn"
            >
              Suivant <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={() => setConfirmSubmit(true)}
              className="bg-emerald-600 hover:bg-emerald-700"
              disabled={submitting}
              data-testid="submit-qcm-btn"
            >
              <Send className="w-4 h-4 mr-2" /> Terminer
            </Button>
          )}
        </div>

        {/* Unanswered warning */}
        {!allAnswered && currentIndex === questions.length - 1 && (
          <div className="flex items-center gap-2 justify-center mt-4 text-amber-600 text-sm">
            <AlertTriangle className="w-4 h-4" />
            <span>{questions.length - answeredCount} question{questions.length - answeredCount > 1 ? 's' : ''} sans réponse</span>
          </div>
        )}
      </div>

      {/* Confirm Submit Dialog */}
      {confirmSubmit && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="confirm-submit-dialog">
          <div className="bg-white rounded-2xl p-6 max-w-sm mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-slate-800 mb-2">Confirmer l'envoi ?</h3>
            <p className="text-sm text-slate-500 mb-1">
              Vous avez répondu à {answeredCount} question{answeredCount > 1 ? 's' : ''} sur {questions.length}.
            </p>
            {!allAnswered && (
              <p className="text-sm text-amber-600 mb-4">
                Attention : {questions.length - answeredCount} question{questions.length - answeredCount > 1 ? 's' : ''} sans réponse.
              </p>
            )}
            <div className="flex gap-3 mt-4">
              <Button variant="outline" onClick={() => setConfirmSubmit(false)} className="flex-1">
                Annuler
              </Button>
              <Button onClick={handleSubmit} className="flex-1 bg-emerald-600 hover:bg-emerald-700" disabled={submitting} data-testid="confirm-submit-btn">
                {submitting ? 'Envoi...' : 'Envoyer'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QCMPassagePage;
